import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, getDocs, serverTimestamp, query, orderBy, where, deleteDoc } from 'firebase/firestore';
import { 
    FaDollarSign, FaExclamationTriangle, FaWrench, 
    FaSave, FaPlus, FaTrash, FaBuilding, FaPhone, FaGlobe, FaInstagram, FaWhatsapp 
} from 'react-icons/fa';
import { 
    PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Color palette for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const StatCard = ({ title, value, icon, color, subText }) => (
    <div className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all group overflow-hidden relative`}>
        <div className={`absolute top-0 left-0 w-1 h-full ${color}`}></div>
        <div className="flex items-center justify-between z-10 relative">
            <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider">{title}</p>
                <h3 className="text-3xl font-bold text-gray-800 mt-2">{value}</h3>
                {subText && <p className="text-xs text-gray-400 mt-1">{subText}</p>}
            </div>
            <div className={`p-3 rounded-xl bg-gray-50 text-gray-600 group-hover:text-white transition-colors duration-300 ${color.replace('bg-', 'group-hover:bg-').replace('border-', 'group-hover:bg-')}`}>
                {icon}
            </div>
        </div>
    </div>
);

const CompanyProfilePage = () => {
    const { userData } = useAuth();
    const [companyInfo, setCompanyInfo] = useState({
        companyName: '',
        companyPhone: '',
        companyWebsite: { value: '', isVisible: true },
        companyContactPhone: { value: '', isVisible: true },
        companyInstagram: { value: '', isVisible: true },
        companyWhatsapp: { value: '', isVisible: true },
    });
    const [financials, setFinancials] = useState({ revenue: 0, pending: 0, expenses: 0 });
    const [expenseList, setExpenseList] = useState([]);
    const [expenseCategoryData, setExpenseCategoryData] = useState([]);
    
    const [newExpense, setNewExpense] = useState({ description: '', amount: '', category: 'Bakım' });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview'); // overview, expenses, settings

    const expensesCollectionRef = useMemo(() => {
        if (!userData?.companyProfileId) return null;
        return collection(db, 'companies', userData.companyProfileId, 'expenses');
    }, [userData]);

    const fetchData = useCallback(async () => {
        if (!userData?.companyProfileId) return;
        setLoading(true);

        try {
            // 1. Fetch Company Info
            const companyDocRef = doc(db, 'companies', userData.companyProfileId);
            const companySnap = await getDoc(companyDocRef);
            if (companySnap.exists()) {
                const data = companySnap.data();
                setCompanyInfo({
                    companyName: data.companyName || '',
                    companyPhone: data.companyPhone || '',
                    companyWebsite: data.companyWebsite || { value: '', isVisible: true },
                    companyContactPhone: data.companyContactPhone || { value: '', isVisible: true },
                    companyInstagram: data.companyInstagram || { value: '', isVisible: true },
                    companyWhatsapp: data.companyWhatsapp || { value: '', isVisible: true },
                });
            }

            // 2. Calculate Revenue & Pending (from Rentals)
            const rentalsQuery = query(collection(db, 'rentals'), where('companyId', '==', userData.companyProfileId));
            const rentalsSnap = await getDocs(rentalsQuery);
            let totalRevenue = 0;
            let pendingPayments = 0;
            rentalsSnap.forEach(doc => {
                const rental = doc.data();
                totalRevenue += Number(rental.paidAmount) || 0;
                // If total price exists but not fully paid
                const total = Number(rental.totalPrice) || 0;
                const paid = Number(rental.paidAmount) || 0;
                if (rental.paymentStatus !== 'Ödendi') {
                    pendingPayments += (total - paid);
                }
            });

            // 3. Fetch Expenses
            if (expensesCollectionRef) {
                const expensesQuery = query(expensesCollectionRef, orderBy('date', 'desc'));
                const expensesSnap = await getDocs(expensesQuery);
                
                let totalExpenses = 0;
                const categoryStats = {};
                
                const expenses = expensesSnap.docs.map(doc => {
                    const data = doc.data();
                    const amount = Number(data.amount) || 0;
                    totalExpenses += amount;
                    
                    // Categorize
                    const cat = data.category || 'Diğer';
                    categoryStats[cat] = (categoryStats[cat] || 0) + amount;
                    
                    return { id: doc.id, ...data };
                });

                setExpenseList(expenses);
                setFinancials({ revenue: totalRevenue, pending: pendingPayments, expenses: totalExpenses });

                // Prepare Chart Data
                const chartData = Object.keys(categoryStats).map(key => ({
                    name: key,
                    value: categoryStats[key]
                }));
                setExpenseCategoryData(chartData);
            }

        } catch (error) {
            console.error("Error fetching company data:", error);
        } finally {
            setLoading(false);
        }
    }, [userData, expensesCollectionRef]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleAddExpense = async (e) => {
        e.preventDefault();
        if (!newExpense.description || Number(newExpense.amount) <= 0) return;
        
        try {
            await addDoc(expensesCollectionRef, { 
                ...newExpense, 
                amount: Number(newExpense.amount), 
                date: serverTimestamp() 
            });
            setNewExpense({ description: '', amount: '', category: 'Bakım' });
            fetchData(); 
            alert("Harcama eklendi.");
        } catch (error) {
            console.error("Error adding expense:", error);
        }
    };
    
    const handleDeleteExpense = async (id) => {
        if(!window.confirm("Bu harcamayı silmek istiyor musunuz?")) return;
        try {
            await deleteDoc(doc(expensesCollectionRef, id));
            fetchData();
        } catch (error) {
            console.error("Error deleting expense:", error);
        }
    }

    const handleSaveCompanyInfo = async () => {
        if (!userData?.companyProfileId) return;
        setLoading(true);
        try {
            const companyDocRef = doc(db, 'companies', userData.companyProfileId);
            await updateDoc(companyDocRef, {
                ...companyInfo,
                updatedAt: serverTimestamp()
            });
            alert('Şirket bilgileri güncellendi!');
        } catch (error) {
            console.error("Error updating company info:", error);
            alert('Şirket bilgileri güncellenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !companyInfo.companyName) return (
        <div className="flex justify-center items-center h-screen text-gray-500">
            <FaWrench className="animate-spin mr-2"/> Yükleniyor...
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    <div className="flex justify-between items-center h-16">
                        <h1 className="text-2xl font-bold text-gray-900">Şirket Yönetim Paneli</h1>
                        <div className="flex space-x-4">
                            <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'overview' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>Genel Bakış</button>
                            <button onClick={() => setActiveTab('expenses')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'expenses' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>Harcamalar</button>
                            <button onClick={() => setActiveTab('settings')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'settings' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>Ayarlar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
                
                {/* Financial Summary Cards (Always Visible) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <StatCard 
                        title="Toplam Tahsilat" 
                        value={`${financials.revenue.toLocaleString('tr-TR')} ₺`} 
                        icon={<FaDollarSign size={24}/>} 
                        color="bg-green-500" 
                        subText="Kiralama Gelirleri"
                    />
                    <StatCard 
                        title="Bekleyen Ödemeler" 
                        value={`${financials.pending.toLocaleString('tr-TR')} ₺`} 
                        icon={<FaExclamationTriangle size={24}/>} 
                        color="bg-yellow-500"
                        subText="Tahsil edilmemiş kiralama bedelleri"
                    />
                    <StatCard 
                        title="Toplam Gider" 
                        value={`${financials.expenses.toLocaleString('tr-TR')} ₺`} 
                        icon={<FaWrench size={24}/>} 
                        color="bg-red-500"
                        subText="Bakım, Yakıt, Sigorta vb."
                    />
                </div>

                {/* Content based on Tab */}
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                         {/* Expense Distribution Chart */}
                         <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-6">Gider Dağılımı</h3>
                            {expenseCategoryData.length > 0 ? (
                                <div className="h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie 
                                                data={expenseCategoryData} 
                                                cx="50%" cy="50%" 
                                                innerRadius={60} 
                                                outerRadius={100} 
                                                paddingAngle={5} 
                                                dataKey="value"
                                            >
                                                {expenseCategoryData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip formatter={(value) => `${value} ₺`} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="h-[300px] flex items-center justify-center text-gray-400">
                                    Veri yok
                                </div>
                            )}
                        </div>

                        {/* Recent Expenses List (Short) */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900">Son Harcamalar</h3>
                                <button onClick={() => setActiveTab('expenses')} className="text-sm text-blue-600 hover:underline">Tümünü Gör</button>
                            </div>
                            <div className="space-y-4">
                                {expenseList.slice(0, 5).map(expense => (
                                    <div key={expense.id} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition border border-gray-100">
                                        <div>
                                            <p className="font-semibold text-gray-800">{expense.description}</p>
                                            <p className="text-xs text-gray-500">
                                                {expense.date?.seconds ? new Date(expense.date.seconds * 1000).toLocaleDateString() : '-'} • {expense.category}
                                            </p>
                                        </div>
                                        <span className="font-mono font-bold text-red-600">
                                            -{expense.amount} ₺
                                        </span>
                                    </div>
                                ))}
                                {expenseList.length === 0 && <p className="text-gray-500 text-center py-4">Harcama kaydı bulunamadı.</p>}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'expenses' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Expense Form */}
                        <div className="lg:col-span-1">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                    <FaPlus className="mr-2 text-blue-600"/> Yeni Harcama Ekle
                                </h3>
                                <form onSubmit={handleAddExpense} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Harcama Tipi</label>
                                        <select 
                                            value={newExpense.category} 
                                            onChange={e => setNewExpense({...newExpense, category: e.target.value})} 
                                            className="w-full border-gray-300 border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50"
                                        >
                                            <option>Bakım</option>
                                            <option>Yakıt</option>
                                            <option>Sigorta</option>
                                            <option>Vergi</option>
                                            <option>Personel</option>
                                            <option>Diğer</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                                        <input 
                                            type="text" 
                                            placeholder="Örn: 34ABC12 Lastik Değişimi" 
                                            value={newExpense.description} 
                                            onChange={e => setNewExpense({...newExpense, description: e.target.value})} 
                                            className="w-full border-gray-300 border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tutar (TL)</label>
                                        <input 
                                            type="number" 
                                            placeholder="0.00" 
                                            value={newExpense.amount} 
                                            onChange={e => setNewExpense({...newExpense, amount: e.target.value})} 
                                            className="w-full border-gray-300 border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                        />
                                    </div>
                                    <button type="submit" className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition shadow-lg mt-2">
                                        Kaydet
                                    </button>
                                </form>
                            </div>
                        </div>

                        {/* Full Expense List Table */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="p-6 border-b border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900">Harcama Geçmişi</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                            <tr>
                                                <th className="px-6 py-4 font-semibold">Tarih</th>
                                                <th className="px-6 py-4 font-semibold">Kategori</th>
                                                <th className="px-6 py-4 font-semibold">Açıklama</th>
                                                <th className="px-6 py-4 font-semibold text-right">Tutar</th>
                                                <th className="px-6 py-4 font-semibold text-center">İşlem</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {expenseList.map(expense => (
                                                <tr key={expense.id} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                                                        {expense.date?.seconds ? new Date(expense.date.seconds * 1000).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                                            {expense.category}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-800">{expense.description}</td>
                                                    <td className="px-6 py-4 text-sm font-mono font-bold text-red-600 text-right">
                                                        -{Number(expense.amount).toLocaleString('tr-TR')} ₺
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button 
                                                            onClick={() => handleDeleteExpense(expense.id)}
                                                            className="text-red-400 hover:text-red-600 p-2 transition"
                                                        >
                                                            <FaTrash size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {expenseList.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-10 text-center text-gray-400">
                                                        Henüz harcama kaydı yok.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                         <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FaBuilding className="mr-3 text-gray-400"/> Şirket Bilgileri
                        </h3>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Şirket Adı</label>
                                <input type="text" value={companyInfo.companyName} onChange={e => setCompanyInfo({...companyInfo, companyName: e.target.value})} className="w-full border-gray-300 border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition"/>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Şirket Telefonu (Dahili)</label>
                                    <div className="relative">
                                        <FaPhone className="absolute left-3 top-3.5 text-gray-400"/>
                                        <input type="text" value={companyInfo.companyPhone} onChange={e => setCompanyInfo({...companyInfo, companyPhone: e.target.value})} className="w-full pl-10 border-gray-300 border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition"/>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">İletişim Telefonu (QR)</label>
                                    <div className="relative">
                                        <FaPhone className="absolute left-3 top-3.5 text-gray-400"/>
                                        <input type="text" value={companyInfo.companyContactPhone.value} onChange={e => setCompanyInfo(prev => ({ ...prev, companyContactPhone: { ...prev.companyContactPhone, value: e.target.value } }))} className="w-full pl-10 border-gray-300 border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition"/>
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        <input type="checkbox" checked={companyInfo.companyContactPhone.isVisible} onChange={e => setCompanyInfo(prev => ({ ...prev, companyContactPhone: { ...prev.companyContactPhone, isVisible: e.target.checked } }))} className="rounded text-blue-600 focus:ring-blue-500"/>
                                        <label className="ml-2 text-sm text-gray-600">QR Sayfasında Göster</label>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Web Sitesi</label>
                                <div className="relative">
                                    <FaGlobe className="absolute left-3 top-3.5 text-gray-400"/>
                                    <input type="text" value={companyInfo.companyWebsite.value} onChange={e => setCompanyInfo(prev => ({ ...prev, companyWebsite: { ...prev.companyWebsite, value: e.target.value } }))} className="w-full pl-10 border-gray-300 border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition"/>
                                </div>
                                <div className="mt-2 flex items-center">
                                    <input type="checkbox" checked={companyInfo.companyWebsite.isVisible} onChange={e => setCompanyInfo(prev => ({ ...prev, companyWebsite: { ...prev.companyWebsite, isVisible: e.target.checked } }))} className="rounded text-blue-600 focus:ring-blue-500"/>
                                    <label className="ml-2 text-sm text-gray-600">QR Sayfasında Göster</label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                                    <div className="relative">
                                        <FaInstagram className="absolute left-3 top-3.5 text-gray-400"/>
                                        <input type="text" value={companyInfo.companyInstagram.value} onChange={e => setCompanyInfo(prev => ({ ...prev, companyInstagram: { ...prev.companyInstagram, value: e.target.value } }))} className="w-full pl-10 border-gray-300 border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition" placeholder="@username"/>
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        <input type="checkbox" checked={companyInfo.companyInstagram.isVisible} onChange={e => setCompanyInfo(prev => ({ ...prev, companyInstagram: { ...prev.companyInstagram, isVisible: e.target.checked } }))} className="rounded text-blue-600 focus:ring-blue-500"/>
                                        <label className="ml-2 text-sm text-gray-600">QR Sayfasında Göster</label>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                                    <div className="relative">
                                        <FaWhatsapp className="absolute left-3 top-3.5 text-gray-400"/>
                                        <input type="text" value={companyInfo.companyWhatsapp.value} onChange={e => setCompanyInfo(prev => ({ ...prev, companyWhatsapp: { ...prev.companyWhatsapp, value: e.target.value } }))} className="w-full pl-10 border-gray-300 border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition" placeholder="+905..."/>
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        <input type="checkbox" checked={companyInfo.companyWhatsapp.isVisible} onChange={e => setCompanyInfo(prev => ({ ...prev, companyWhatsapp: { ...prev.companyWhatsapp, isVisible: e.target.checked } }))} className="rounded text-blue-600 focus:ring-blue-500"/>
                                        <label className="ml-2 text-sm text-gray-600">QR Sayfasında Göster</label>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-100">
                                <button onClick={handleSaveCompanyInfo} disabled={loading} className="w-full flex items-center justify-center bg-blue-600 text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition shadow-lg hover:shadow-xl">
                                    <FaSave className="mr-2"/> Değişiklikleri Kaydet
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyProfilePage;